import { DateTime } from 'luxon';
import { store, storeActions, Measurements } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css, nothing } from '../utility/lit-entity';
import { sharedStyles } from '../styles/shared-styles';
import '../bins/parts/bin-profile-index';
import '../bins/parts/bin-medicated-banner';
import '../bins/parts/bin-work-order-banner';
import '../bins/props/bin-current-weight';
import '../bins/props/bin-current-report-date';
import '../common/bn-banner';
import '../common/bn-button';
import '../common/bn-property';
import '../organizations/parts/organization-ancestors-bread-crumbs';
import './qr-layout';

import { actions } from './qr-context';

class QrBin extends LitEntity {
  static get properties() {
    return {
      nextOrderEntity: { type: Object },
      orderMetricsEntity: { type: Object },
      feedEntity: { type: Object },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      h1 {
        margin: 0;
        line-height: 1.25em;
      }
      .banners {
        margin: 0 -1rem;
      }
      .top-actions {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 1rem;
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html`<bn-loading></bn-loading>`;
    }

    const binLevelHref = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/bin-level-latest').href || '';
    const ownerHref = this.entity.getSubEntityByRel('https://api.binsentry.com/rel/owner').href || '';

    return html`
      <qr-layout>
        <div class="top-actions">
          <bn-button @click="${this.onScan}" variant="text" >Scan Bin QR Code</bn-button>
        </div>
        <div>
          <organization-ancestors-bread-crumbs href="${ownerHref}"></organization-ancestors-bread-crumbs>
          <h1>
            <bin-name .href="${this.href}"></bin-name>
            &dash;
            <bin-level-metric .href="${this.href}"></bin-level-metric>
          </h1>
          <bn-property label="Last Reading" class="date">
            <bin-current-report-date href="${binLevelHref}"></bin-current-report-date>
          </bn-property>
        </div>

        <div class="profile">
          <bin-profile-index .href="${this.href}"></bin-profile-index>
        </div>

        <div class="banners">
          <bin-medicated-banner .href="${this.href}"></bin-medicated-banner>
          ${this.renderNextOrderBanner()}
          ${this.renderPotentialOverfillBanner()}
          <bin-work-order-banner .href="${this.href}"></bin-work-order-banner>
        </div>

        <div slot="actions">
          <bn-button @click="${this.viewBin}">View Bin Details</bn-button>
        </div>
      </qr-layout>
    `;
  }

  renderNextOrderBanner() {
    const deliveryDate = this.nextOrderEntity?.properties?.deliveryDate;

    if (!deliveryDate) {
      return nothing;
    }

    const date = DateTime.fromISO(deliveryDate);

    const volume = this.nextOrderEntity?.properties.quantity;
    const bulkDensity = this.feedEntity?.properties.bulkDensity;
    const totalVolume = this.entity?.properties.volumes.fullTotal;
    const showQuantity = volume && totalVolume && bulkDensity;
    const measurement = Measurements.get('orderAmount');

    return html`
      <bn-banner severity="success" icon="fas fa-truck">
        <div>
          Next Delivery: ${date.toFormat('MMM d, h:mm a')}
        </div>
        ${showQuantity ? html`
          <div>
            Quantity: ${measurement.html({ volume, bulkDensity, totalVolume })}
          </div>
        ` : nothing}
      </bn-banner>
    `;
  }

  renderPotentialOverfillBanner() {
    const potentialOverfill = this.orderMetricsEntity?.properties?.potentialOverfill;

    if (!potentialOverfill) {
      return nothing;
    }

    return html`
      <bn-banner severity="critical">Overfill Predicted</bn-banner>
    `;
  }

  onStart() {
    actions.confirmOrder();
  }

  onScan() {
    actions.scan();
  }

  viewBin() {
    store.dispatch(storeActions.navigate('/bins/show', { binId: this.entity.getLink('self').href }));
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);

    const ownerHref = this.entity?.getSubEntity('https://api.binsentry.com/rel/owner')?.href;
    const nextOrderHref = this.entity?.getSubEntity('https://api.binsentry.com/rel/next-order')?.href;
    const deliveriesHref = this.entity?.getSubEntity('https://api.binsentry.com/rel/deliveries')?.href;
    [
      this.nextOrderEntity,
      this.ownerEntity,
      this.deliveries,
    ] = await Promise.all([
      this.fetchSiren(nextOrderHref, false),
      this.fetchSiren(ownerHref, false),
      this.fetchSiren(deliveriesHref, false),
    ]);

    const orderMetricsHref = this.nextOrderEntity?.getSubEntity('https://api.binsentry.com/rel/order-metrics')?.href;
    const feedHref = this.nextOrderEntity?.getSubEntity('https://api.binsentry.com/rel/feed')?.href;
    [
      this.orderMetricsEntity,
      this.feedEntity,
    ] = await Promise.all([
      this.fetchSiren(orderMetricsHref, false),
      this.fetchSiren(feedHref, false),
    ]);
  }
}

window.customElements.define('qr-bin', QrBin);
